import jsPDF from 'jspdf';
import { Document, Packer, Paragraph, TextRun, BorderStyle } from 'docx';
import type { Organization, OrgProject, Lang } from './partnerTypes';

// ── Shared types ───────────────────────────────────────────────────────────────

interface ExportField {
  label: string;
  value: string;
}

interface ExportSection {
  heading: string;
  fields: ExportField[];
}

// ── Shared PDF renderer ────────────────────────────────────────────────────────

function renderPDF(title: string, sections: ExportSection[], filename: string): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageW = 210;
  const margin = 20;
  const contentW = pageW - margin * 2;
  let y = 28;

  // Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(26, 46, 74);
  const titleLines = doc.splitTextToSize(title, contentW) as string[];
  doc.text(titleLines, margin, y);
  y += titleLines.length * 8 + 3;

  // Accent underline
  doc.setDrawColor(26, 46, 74);
  doc.setLineWidth(0.8);
  doc.line(margin, y, margin + 40, y);
  y += 9;

  for (const section of sections) {
    if (section.fields.length === 0) continue;
    if (y > 265) { doc.addPage(); y = 20; }

    // Section heading
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(140, 140, 140);
    doc.text(section.heading.toUpperCase(), margin, y);
    y += 1;
    doc.setDrawColor(220, 220, 220);
    doc.setLineWidth(0.3);
    doc.line(margin, y + 1, pageW - margin, y + 1);
    y += 6;

    for (const field of section.fields) {
      if (y > 270) { doc.addPage(); y = 20; }

      const labelW = 58;
      const valueX = margin + labelW + 2;
      const valueW = contentW - labelW - 2;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(90, 90, 90);
      doc.text(field.label, margin, y);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(30, 30, 30);
      const valueLines = doc.splitTextToSize(field.value, valueW) as string[];
      doc.text(valueLines, valueX, y);

      y += Math.max(valueLines.length * 5, 5) + 3;
    }
    y += 5;
  }

  doc.save(filename);
}

// ── Shared Word renderer ───────────────────────────────────────────────────────

async function renderWord(title: string, sections: ExportSection[], filename: string): Promise<void> {
  const children: Paragraph[] = [];

  children.push(
    new Paragraph({
      children: [new TextRun({ text: title, bold: true, size: 32, color: '1a2e4a' })],
      spacing: { after: 240 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 6, color: '1a2e4a' } },
    })
  );

  for (const section of sections) {
    if (section.fields.length === 0) continue;

    children.push(
      new Paragraph({
        children: [new TextRun({ text: section.heading.toUpperCase(), bold: true, size: 18, color: '888888', allCaps: true })],
        spacing: { before: 320, after: 80 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 3, color: 'dddddd' } },
      })
    );

    for (const field of section.fields) {
      children.push(
        new Paragraph({
          children: [
            new TextRun({ text: `${field.label}: `, bold: true, size: 20, color: '444444' }),
            new TextRun({ text: field.value, size: 20, color: '1a1a1a' }),
          ],
          spacing: { after: 100 },
        })
      );
    }
  }

  const wordDoc = new Document({ sections: [{ children }] });
  const blob = await Packer.toBlob(wordDoc);
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Organisation export ────────────────────────────────────────────────────────

function buildOrgSections(org: Organization, projects: OrgProject[], lang: Lang): { title: string; sections: ExportSection[] } {
  const title = (lang === 'en' ? org.name_en : org.name_ro) || org.name_en || org.name_ro || 'Organisation';

  const generalFields: ExportField[] = [];
  if (org.name_en && org.name_ro && org.name_en !== org.name_ro) {
    generalFields.push({ label: 'Name (EN)', value: org.name_en });
    generalFields.push({ label: 'Name (RO)', value: org.name_ro });
  }
  if (org.idno) generalFields.push({ label: 'IDNO', value: org.idno });
  if (org.region_city) generalFields.push({ label: 'Region / City', value: org.region_city });
  if (org.registration_date) generalFields.push({ label: 'Registration Date', value: org.registration_date });
  if (org.legal_address) generalFields.push({ label: 'Legal Address', value: org.legal_address });

  const contactFields: ExportField[] = [];
  if (org.email) contactFields.push({ label: 'Email', value: org.email });
  if (org.phone) contactFields.push({ label: 'Phone', value: org.phone });
  if (org.social_facebook) contactFields.push({ label: 'Facebook', value: org.social_facebook });
  if (org.social_instagram) contactFields.push({ label: 'Instagram', value: org.social_instagram });
  if (org.social_linkedin) contactFields.push({ label: 'LinkedIn', value: org.social_linkedin });

  const directorFields: ExportField[] = [];
  if (org.director_name) directorFields.push({ label: 'Full Name', value: org.director_name });
  if (org.director_position) directorFields.push({ label: 'Position', value: org.director_position });
  if (org.director_phone) directorFields.push({ label: 'Phone', value: org.director_phone });
  if (org.director_email) directorFields.push({ label: 'Email', value: org.director_email });

  const orgContactFields: ExportField[] = [];
  if (org.contact_name) orgContactFields.push({ label: 'Full Name', value: org.contact_name });
  if (org.contact_position) orgContactFields.push({ label: 'Position', value: org.contact_position });
  if (org.contact_phone) orgContactFields.push({ label: 'Phone', value: org.contact_phone });
  if (org.contact_email) orgContactFields.push({ label: 'Email', value: org.contact_email });

  const descFields: ExportField[] = [];
  if (org.description_en && org.description_ro && org.description_en !== org.description_ro) {
    descFields.push({ label: 'Description (EN)', value: org.description_en });
    descFields.push({ label: 'Description (RO)', value: org.description_ro });
  } else if (org.description_en) {
    descFields.push({ label: 'Description', value: org.description_en });
  } else if (org.description_ro) {
    descFields.push({ label: 'Description', value: org.description_ro });
  }
  if (org.activity_domains) {
    descFields.push({ label: 'Domenii de activitate', value: org.activity_domains });
  }
  if (org.org_capacity) {
    descFields.push({ label: 'Capacitate organizațională', value: org.org_capacity });
  }
  if (org.work_areas_en && org.work_areas_ro && org.work_areas_en !== org.work_areas_ro) {
    descFields.push({ label: 'Work Areas (EN)', value: org.work_areas_en });
    descFields.push({ label: 'Work Areas (RO)', value: org.work_areas_ro });
  } else if (org.work_areas_en) {
    descFields.push({ label: 'Work Areas', value: org.work_areas_en });
  } else if (org.work_areas_ro) {
    descFields.push({ label: 'Work Areas', value: org.work_areas_ro });
  }
  if (org.grant_themes) {
    descFields.push({ label: 'Tema (temele) preconizată(e) pentru proiectul (proiectele) de grant', value: org.grant_themes });
  }

  const partnerFields: ExportField[] = [];
  if (org.partners_donors) partnerFields.push({ label: 'Partners & Donors', value: org.partners_donors });

  const sections: ExportSection[] = [];
  if (generalFields.length > 0) sections.push({ heading: 'General Information', fields: generalFields });
  if (contactFields.length > 0) sections.push({ heading: 'Contact Information', fields: contactFields });
  if (directorFields.length > 0) sections.push({ heading: 'Director / Manager', fields: directorFields });
  if (orgContactFields.length > 0) sections.push({ heading: 'Contact Person (Projects)', fields: orgContactFields });
  if (descFields.length > 0) sections.push({ heading: 'Description', fields: descFields });
  if (partnerFields.length > 0) sections.push({ heading: 'Partners & Donors', fields: partnerFields });

  // Projects section
  if (projects.length > 0) {
    const projectFields: ExportField[] = [];
    projects.forEach((p, idx) => {
      const pName = (lang === 'en' ? p.name_en : p.name_ro) || p.name_en || p.name_ro || `Project ${idx + 1}`;
      const parts: string[] = [];
      if (p.year) {
        const yearLabel = p.year_end && p.year_end !== p.year
          ? `${p.year} – ${p.year_end}`
          : String(p.year);
        parts.push(`Period: ${yearLabel}`);
      }
      if (p.donor) parts.push(`Donor: ${p.donor}`);
      if (p.budget) parts.push(`Budget: ${p.budget}`);
      const desc = (lang === 'en' ? p.description_en : p.description_ro) || p.description_en || p.description_ro;
      if (desc) parts.push(`Description: ${desc}`);
      if (p.manager_name) parts.push(`Manager: ${p.manager_name}${p.manager_position ? `, ${p.manager_position}` : ''}${p.manager_phone ? ` | ${p.manager_phone}` : ''}${p.manager_email ? ` | ${p.manager_email}` : ''}`);
      if (p.contact_name) parts.push(`Contact: ${p.contact_name}${p.contact_position ? `, ${p.contact_position}` : ''}${p.contact_phone ? ` | ${p.contact_phone}` : ''}${p.contact_email ? ` | ${p.contact_email}` : ''}`);
      projectFields.push({ label: pName, value: parts.length > 0 ? parts.join('\n') : '—' });
    });
    sections.push({ heading: `Implemented Projects (${projects.length})`, fields: projectFields });
  }

  return { title, sections };
}

export function exportOrganizationPDF(org: Organization, projects: OrgProject[], lang: Lang): void {
  const { title, sections } = buildOrgSections(org, projects, lang);
  const safe = title.replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 40);
  renderPDF(title, sections, `${safe}_organisation.pdf`);
}

export async function exportOrganizationWord(org: Organization, projects: OrgProject[], lang: Lang): Promise<void> {
  const { title, sections } = buildOrgSections(org, projects, lang);
  const safe = title.replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 40);
  await renderWord(title, sections, `${safe}_organisation.docx`);
}

// ── Project card export (kept for individual use) ─────────────────────────────

function buildProjectSections(project: OrgProject, lang: Lang): { title: string; sections: ExportSection[] } {
  const title =
    (lang === 'en' ? project.name_en : project.name_ro) ||
    project.name_en || project.name_ro || 'Untitled Project';

  const mainFields: ExportField[] = [];
  if (project.name_en && project.name_ro && project.name_en !== project.name_ro) {
    mainFields.push({ label: 'Project Name (EN)', value: project.name_en });
    mainFields.push({ label: 'Project Name (RO)', value: project.name_ro });
  } else if (project.name_en) {
    mainFields.push({ label: 'Project Name', value: project.name_en });
  } else if (project.name_ro) {
    mainFields.push({ label: 'Project Name', value: project.name_ro });
  }
  if (project.year) {
    const yearLabel = project.year_end && project.year_end !== project.year
      ? `${project.year} – ${project.year_end}`
      : String(project.year);
    mainFields.push({ label: 'Period of Implementation', value: yearLabel });
  }
  if (project.donor) mainFields.push({ label: 'Donor / Funder', value: project.donor });
  if (project.budget) mainFields.push({ label: 'Budget / Grant Amount', value: project.budget });
  if (project.description_en && project.description_ro && project.description_en !== project.description_ro) {
    mainFields.push({ label: 'Description (EN)', value: project.description_en });
    mainFields.push({ label: 'Description (RO)', value: project.description_ro });
  } else if (project.description_en) {
    mainFields.push({ label: 'Description', value: project.description_en });
  } else if (project.description_ro) {
    mainFields.push({ label: 'Description', value: project.description_ro });
  }

  const managerFields: ExportField[] = [];
  if (project.manager_name) managerFields.push({ label: 'Full Name', value: project.manager_name });
  if (project.manager_position) managerFields.push({ label: 'Position', value: project.manager_position });
  if (project.manager_phone) managerFields.push({ label: 'Phone', value: project.manager_phone });
  if (project.manager_email) managerFields.push({ label: 'Email', value: project.manager_email });

  const contactFields: ExportField[] = [];
  if (project.contact_name) contactFields.push({ label: 'Full Name', value: project.contact_name });
  if (project.contact_position) contactFields.push({ label: 'Position', value: project.contact_position });
  if (project.contact_phone) contactFields.push({ label: 'Phone', value: project.contact_phone });
  if (project.contact_email) contactFields.push({ label: 'Email', value: project.contact_email });

  const sections: ExportSection[] = [{ heading: 'Project Information', fields: mainFields }];
  if (managerFields.length > 0) sections.push({ heading: 'Director / Manager', fields: managerFields });
  if (contactFields.length > 0) sections.push({ heading: 'Contact Person', fields: contactFields });

  return { title, sections };
}

export function exportProjectPDF(project: OrgProject, lang: Lang): void {
  const { title, sections } = buildProjectSections(project, lang);
  const safe = title.replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 40);
  renderPDF(title, sections, `${safe}_project.pdf`);
}

export async function exportProjectWord(project: OrgProject, lang: Lang): Promise<void> {
  const { title, sections } = buildProjectSections(project, lang);
  const safe = title.replace(/[^a-z0-9]/gi, '_').toLowerCase().slice(0, 40);
  await renderWord(title, sections, `${safe}_project.docx`);
}
